import {
  ERROR_CODE,
  IInteraction,
  INTERACTION_DIRECTION_TYPES,
  LOG_LEVEL,
  USER_TYPES,
} from '@amc-technology/davinci-api';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { SENDER_TYPE } from '../Model/Enums/SenderType';
import { StudioConfigService } from './studio-config.service';
import { CID } from 'src/app/Model/Types/CID';
import { firstValueFrom } from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class CommunicationService {
  private orgId?: string; // SAlesforce Organization Id
  private orgUrl?: string; // Salesforce Organization Url
  private callCenterApiName?: string; // DaVinci Call Contact Center name in Salesforce
  private agentId?: string; // Salesforce User Id
  private ctiId = '';
  private readonly interactionMessageIdMap: Map<string, string[]> = new Map<
    string,
    string[]
  >(); // Map of interactionId to messageId

  log: (
    logLevel: LOG_LEVEL,
    fName: string,
    message: string,
    object?: any,
    errorCode?: ERROR_CODE,
    localTime?: Date
  ) => void = () => {};

  constructor(
    private readonly loggerService: LoggerService,
    private readonly scs: StudioConfigService,
    private readonly http: HttpClient
  ) {
    this.log = this.loggerService.log.bind(this.loggerService);
  }

  /**
   * This function initializes the communication service with the required parameters.
   *
   * @param {string} orgId The Salesforce Organization Id from Salesforce
   * @param {string} orgUrl The Salesforce Organization Url from Salesforce
   * @param {string} callCenterApiName The Salesforce Call Center API Name from Salesforce
   * @param {string} agentId The Salesforce User Id from Salesforce
   * @memberof CommunicationService
   */
  initialize(
    orgId: string,
    orgUrl: string,
    callCenterApiName: string,
    agentId: string
  ): void {
    const fname = 'Comm.initialize';
    try {
      this.log(LOG_LEVEL.Debug, fname, 'Initializing communication service with data from Salesforce Org', { orgId, orgUrl, callCenterApiName, agentId });
      this.orgId = orgId;
      this.orgUrl = orgUrl;
      this.callCenterApiName = callCenterApiName;
      this.agentId = agentId;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error initializing communication service', error);
    }
  }

  setCtiId(ctiId: string): void {
    const fname = 'setCtiId';
    try {
      this.ctiId = ctiId;
      this.log(
        LOG_LEVEL.Debug,
        fname,
        'Setting CTI Id for communication service',
        { ctiId }
      );
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        fname,
        'Error setting CTI Id for communication service',
        error
      );
    }
  }

  /**
   * This function creates a VoiceCall Record in Salesforce via the communication service and returns the VoiceCallId.
   * If the call already exists, it will return the existing VoiceCallId.
   *
   * @param {CID} cId unique process ID
   * @param {IInteraction} interaction from CTI
   * @param {string} interaction.interactionId The interactionId from CTI
   * @param {string} interaction.details.fields.DialedPhone.Value' The DNIS from CTI
   * @param {string} interaction.details.fields.Phone.Value The ANI from CTI
   * @param {date} startTime The time the alerting interaction was recieved
   * @param {string} vendorCallKey The unique call Id that will be used for this scenario in DEG
   * @param {boolean} isInbound Determines if the call is inbound or outbound
   * @param {string} callAttributes CAD to be passed to the Salesforce Voice Call Record
   *
   * @return {*} {Promise<string>} containing VoiceCallId needed for ScreenPop
   * @memberof CommunicationService
   */
  async createCall(
    cId: CID,
    interaction: IInteraction,
    startTime: Date,
    vendorCallKey: string,
    isInbound: boolean,
    callAttributes?: string
  ): Promise<string> {
    const fname = `Comm.createCall(${cId})`;
    const key: string = vendorCallKey ?? this.formatInteractionId(interaction.interactionId);
    let requestStart = Date.now();
    const timeoutMs = this.scs.requestTimeout ?? 5000;

    try {

      if (!this.agentId) {
        this.log(LOG_LEVEL.Error, fname, `No agentId from Salesforce to send to DEG for interaction ${interaction.interactionId}`);
        return key;
      }

      this.log(LOG_LEVEL.Debug, fname, `Creating call for interaction ${interaction.interactionId}`);

      const to = interaction.direction === INTERACTION_DIRECTION_TYPES.Inbound ? this.ctiId : interaction.details?.fields.Phone.Value;
      const from = interaction.direction === INTERACTION_DIRECTION_TYPES.Inbound ? interaction.details?.fields.Phone.Value : this.ctiId;

      const payload = {
        to: to,
        from: from,
        vendorCallKey: key,
        startTime: startTime,
        participants: [
          {
            participantKey: from,
            type: 'END_USER',
          },
        ],
        agentId: this.agentId,
        createCallInSalesforce: isInbound, // Only create the call in Salesforce if it is an inbound
        direction: isInbound ? 'Inbound' : 'Outbound',
        orgId: this.orgId,
        callAttributes: callAttributes ?? undefined
      };

      this.log(LOG_LEVEL.Trace, fname, `Sending create payload with vendorCallKey ${key} for agent ${this.agentId}`, { payload, url: this.scs.communicationServiceUrl });

      requestStart = Date.now();
      const request$ = this.http
        .post<{ voiceCallId: string; errors: any }>(
          this.scs.communicationServiceUrl + '/api/callevent/CreateCall',
          payload,
          {
            headers: {
              AuthToken: this.scs.authToken || '',
            },
          }
        );

      const responsePromise = firstValueFrom(request$);
      responsePromise.then(res => {
        if (Date.now() - requestStart >= timeoutMs) {
          this.log(LOG_LEVEL.Debug, fname, `DEG responded in ${Date.now() - requestStart}ms`, res);
        }
      }).catch(() => { });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`DEG CreateCall timed out after ${timeoutMs}ms`)), timeoutMs)
      );

      const response = await Promise.race([responsePromise, timeoutPromise]);

      if (response?.errors?.length > 0 && response.errors.length !== 1 && response.errors[0] === '') {
        this.log(LOG_LEVEL.Error, fname, 'Errors were returned by Salesforce when the DaVinci communication service attempted to create the Voice Call Record.', response.errors);
      }

      if (response?.voiceCallId) {
        this.log(LOG_LEVEL.Debug, fname, `The Voice Call Record was successfully created by the DaVinci Communication Service. VoiceCallId: ${response.voiceCallId}`, response);
        this.interactionMessageIdMap.set(interaction.interactionId, []);
        return response.voiceCallId;
      } else {
        this.log(LOG_LEVEL.Error, fname, 'Error creating call via communication service. No valid VoiceCallId was received back from DaVinci Communication Service.', response);
        throw new Error('Error creating call via communication service. No valid VoiceCallId was received back from DaVinci Communication Service.');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('timed out')) {
        this.log(LOG_LEVEL.Error, fname, `DEG CreateCall timed out after ${timeoutMs}ms. Returning ${key}`, error);
      } else {
        this.log(LOG_LEVEL.Error, fname, `Error creating call. Returning ${key}`, error);
      }
      return key;
    }
  }

  /**
   * This function updates an existing VoiceCall Record in Salesforce via the communication service and returns the the status of the update.
   *
   * @param {CID} cId unique process ID
   * @param {string} interactionId The interactionId for the interaction that is being used to trigger update
   * @param {string} voiceCallId That call ID returned when the call was created by DEG
   * @param {Date} startTime The time the alerting interaction was recieved
   * @param {Date} endTime The time the disconnected interaction was recieved
   * @param {string} callAttributes CAD to be passed to the Salesforce Voice Call Record
   *
   * @return {*} {Promise<string>} containing status of the update process
   * @memberof CommunicationService
   */
  async updateCall(
    cId: CID,
    interactionId: string,
    voiceCallId: string,
    startTime: Date,
    endTime: Date,
    callAttributes?: string
  ): Promise<void> {
    const fname = `Comm.updateCall(${cId})`;
    let requestStart = Date.now();
    const timeoutMs = this.scs.requestTimeout ?? 5000;
    try {

      if (!this.agentId) {
        this.log(LOG_LEVEL.Error, fname, `No agentId from Salesforce to send to DEG for interaction ${interactionId}`);
        return;
      }

      this.log(LOG_LEVEL.Debug, fname, `Updating call for interaction ${interactionId}`, { voiceCallId: voiceCallId });

      const payload: any = {
        voiceCallId,
        startTime,
        endTime,
        callAttributes: callAttributes ?? undefined,
        agentId: this.agentId,
        orgId: this.orgId,
        isActiveCall: !this.scs.closeCallOnUpdate // using the inverse of the config to make the config easier to understand
      };

      this.log(LOG_LEVEL.Trace, fname, `Sending Update payload with voiceCallId ${voiceCallId} for agent ${this.agentId}`, { payload, url: this.scs.communicationServiceUrl });

      requestStart = Date.now();
      const request$ = this.http
        .patch<{ status: string }>(
          `${this.scs.communicationServiceUrl}/api/callevent/Update`,
          payload,
          {
            headers: {
              AuthToken: this.scs.authToken || '',
            },
          }
        );

      const responsePromise = firstValueFrom(request$);
      responsePromise.then(res => {
        if (Date.now() - requestStart >= timeoutMs) {
          this.log(LOG_LEVEL.Debug, fname, `DEG responded in ${Date.now() - requestStart}ms`, res);
        }
      }).catch(() => { });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`DEG Update timed out after ${timeoutMs}ms`)), timeoutMs)
      );

      const response = await Promise.race([responsePromise, timeoutPromise]);

      this.log(LOG_LEVEL.Debug, fname, 'Response from communication service', response);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('timed out')) {
        this.log(LOG_LEVEL.Error, fname, `DEG Update timed out after ${timeoutMs}ms`, error);
      } else {
        this.log(LOG_LEVEL.Error, fname, 'Error updating call', error);
      }
      throw error;
    }
  }

  /**
   * This function appends a message to the existing transcript in Salesforce via the communication service.
   * The message will not be displayed unless an agent has teh VoiceCall record open for the corresponding call.
   * The messages will not persist if the VoiceCall record is closed because the call was disconnected.
   * TOoDO: Add a way to persist the transcript after the call is completed
   *
   * @param {CID} cId unique process ID
   * @param {IInteraction} interaction from CTI
   * @param {string} interaction.interactionId The interactionId from CTI
   * @param {string} interaction.transcripts.data The message to be appended to the transcript
   * @param {string} interaction.transcripts.id The messageId from the transcription service
   * @param {string} interaction.transcripts.timestamp The timestamp of the message
   * @param {USER_TYPES} interaction.transcripts.context.userType The userType that is sending the message in the transcript
   * @return {*}  {Promise<void>}
   * @memberof CommunicationService
   */
  async sendTranscription(
    cId: CID,
    interaction: IInteraction,
    vendorCallKey?: string
  ): Promise<void> {
    const fname = `Comm.sendTranscription(${cId}):`;
    let requestStart = Date.now();
    const timeoutMs = this.scs.requestTimeout ?? 5000;
    try {

      if (!this.agentId) {
        this.log(LOG_LEVEL.Error, fname, `No agentId from Salesforce to send to DEG for interaction ${interaction.interactionId}`);
        return;
      }


      this.log(LOG_LEVEL.Debug, fname, 'Sending transcription via communication service', { interactionId: interaction.interactionId, messageId: interaction.transcripts?.id, vendorCallKey });
      this.log(LOG_LEVEL.Trace, fname, 'Detailed Transcription information', { interactionId: interaction.interactionId, messageId: interaction.transcripts?.id, startTime: interaction.transcripts?.timestamp, userType: interaction.transcripts?.context.userType, });

      if (!this.interactionMessageIdMap.has(interaction.interactionId)) {
        this.log(LOG_LEVEL.Trace, fname, 'No message map for interaction, VoiceCall must have been made via client side api.', { interactionId: interaction.interactionId });
        this.interactionMessageIdMap.set(interaction.interactionId, []);
      }

      if (this.interactionMessageIdMap.get(interaction.interactionId)?.includes(interaction.transcripts?.id || '')) {
        this.log(LOG_LEVEL.Trace, fname, 'Message already sent', {
          interactionId: interaction.interactionId,
          messageId: interaction.transcripts?.id,
        });
        return;
      }

      const formattedId = vendorCallKey ?? this.formatInteractionId(interaction.interactionId);
      const isAgent = interaction.transcripts?.context.userType === USER_TYPES.Agent;

      requestStart = Date.now();
      const request$ = this.http
        .post<{ response: any }>(
          this.scs.communicationServiceUrl +
            '/api/transcription/CreateTranscript',
          {
            content: interaction.transcripts?.data,
            messageId: interaction.transcripts?.id,
            participantId: isAgent
              ? this.agentId
              : `${formattedId}${SENDER_TYPE.END_USER}`,
            senderType: isAgent
              ? SENDER_TYPE.HUMAN_AGENT
              : SENDER_TYPE.END_USER,
            orgId: this.orgId,
            startTime: interaction.transcripts?.timestamp,
            endTime: new Date(),
            vendorCallKey: formattedId
          },
          {
            headers: {
              AuthToken: this.scs.authToken || '',
            },
          }
        );

      const responsePromise = firstValueFrom(request$);
      responsePromise.then(res => {
        if (Date.now() - requestStart >= timeoutMs) {
          this.log(LOG_LEVEL.Debug, fname, `DEG responded in ${Date.now() - requestStart}ms`, res);
        }
      }).catch(() => {});

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`DEG CreateTranscript timed out after ${timeoutMs}ms`)), timeoutMs)
      );

      const response = await Promise.race([responsePromise, timeoutPromise]);

      this.log(LOG_LEVEL.Debug, fname, 'Response from communication service', response);
      this.interactionMessageIdMap.set(interaction.interactionId, [
        ...(this.interactionMessageIdMap.get(interaction.interactionId) || []),
        interaction.transcripts?.id || '',
      ]);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg.includes('timed out')) {
        this.log(LOG_LEVEL.Error, fname, `DEG CreateTranscript timed out after ${timeoutMs}ms`, error);
      } else {
        this.log(LOG_LEVEL.Error, fname, 'Error sending transcription', error);
      }
    }
  }

  /**
   * This function will take an interactionId and format it so that it can be used in URL by Salesforce.
   *
   * @private
   * @param {string} interactionId
   * @return {*} {string} formattedId
   * @memberof CommunicationService
   */
  formatInteractionId(interactionId: string): string {
    const fname = 'Comm.formatInteractionId';
    try {
      const formattedId = interactionId.replace(/[^a-zA-Z0-9._~-]/g, '');
      this.log(LOG_LEVEL.Loop, fname, 'Formatted interactionId', {
        interactionId,
        formattedId,
      });
      return formattedId;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error formatting interactionId', { interactionId, error });
      throw error;
    }
  }

  /**
   * This function will take an interactionId and format it so that it can be used in URL by Salesforce.
   *
   * @private
   * @param {string} interactionId
   * @return {} {string} callAttributes
   * @memberof CommunicationService
   */
  processVoiceCallRecordToCADMapping(interaction: IInteraction): string {
    const fname = 'Comm.processCADToVoiceCallRecord';

    try {
      this.log(LOG_LEVEL.Information, fname, 'Processing CAD to VoiceCall Record', { interaction });

      const callAttributes: any = {};

      for (const [key, value] of Object.entries(
        this.scs.VoiceCallRecordToCAD || {}
      )) {
        if (typeof value !== 'string') {
          throw new Error('processVoiceCallRecordToCADMapping: got invalid key!');
        }

        if (interaction.details?.fields[value] != null) {
          callAttributes[key] = interaction.details.fields[value].Value;
        }
      }

      if (callAttributes) {
        return JSON.stringify(callAttributes);
      }

      return '';
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error processing CAD to VoiceCall Record', error);
      throw error;
    }
  }
}
